/**
 * WordPress Twee-Story Export
 * Generates embeddable interactive story HTML for WordPress
 * This creates the actual story experience (like the Compiler) for WordPress sites
 */

/**
 * Parse Twee format into passages array
 */
const parseTwee = (twee) => {
  const passages = [];
  const lines = twee.split('\n');
  let currentPassage = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.trim().startsWith(':: ')) {
      if (currentPassage && currentPassage.content.trim()) {
        passages.push(currentPassage);
      }
      const name = line.substring(3).split('[')[0].trim();
      currentPassage = { name, content: '' };
    } else if (currentPassage) {
      currentPassage.content += line + '\n';
    }
  }
  
  if (currentPassage && currentPassage.content.trim()) {
    passages.push(currentPassage);
  }
  
  return passages;
};

/**
 * Process content with Twee/SugarCube/Markdown formatting
 */
const processContent = (text) => {
  let processed = text;
  
  // Mask <a>...</a> blocks so subsequent Markdown substitutions (especially
  // _italic_) don't mangle URLs containing underscores or attribute values
  // like target="_blank". Restored at the end of this function.
  const __anchorMasks = [];
  processed = processed.replace(/<a\s[^>]*>[\s\S]*?<\/a>/gi, (m) => {
    __anchorMasks.push(m);
    return `\u0000ANC${__anchorMasks.length - 1}\u0000`;
  });
  // Process Twee image syntax
  processed = processed.replace(/\[img\[([^\]|]+)(?:\|([^\]]+))?\]\]/gi, (match, url, position) => {
    const pos = position ? position.toLowerCase().trim() : 'center';
    return `<img src="${url}" alt="Story image" class="story-image img-${pos}">`;
  });
  
  // Process Markdown images
  processed = processed.replace(/!\[([^\]]*)\]\(([^)|]+)(?:\|([^)]+))?\)/gi, (match, alt, url, position) => {
    const pos = position ? position.toLowerCase().trim() : 'center';
    return `<img src="${url}" alt="${alt}" class="story-image img-${pos}">`;
  });
  
  // Horizontal rules
  processed = processed.replace(/^[\-\*]{3,}$/gm, '<hr>');
  
  // Convert bullet lists
  const lines = processed.split('\n');
  let inList = false;
  let result = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bulletMatch = line.match(/^[\*\-]\s+(.+)$/);
    
    if (bulletMatch && !line.match(/^[\*\-]{2,}/)) {
      if (!inList) {
        result.push('<ul>');
        inList = true;
      }
      result.push(`<li>${bulletMatch[1]}</li>`);
    } else {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }
      result.push(line);
    }
  }
  if (inList) {
    result.push('</ul>');
  }
  processed = result.join('\n');
  
  // Bold: **text**
  processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // Italic: *text*
  processed = processed.replace(/(?<!<li>|<strong>)\*([^*\n]+)\*(?!<\/strong>)/g, '<em>$1</em>');
  processed = processed.replace(/(?<!<strong>)_([^_\n]+)_(?!<\/strong>)/g, '<em>$1</em>');
  
  // Strikethrough: ~~text~~
  processed = processed.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  
  // SugarCube Headings: !Heading
  processed = processed.replace(/^!{1,6}\s*(.+)$/gm, (match, text) => {
    const level = match.match(/^!+/)[0].length;
    return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
  });
  
  // Markdown Headings: # text
  processed = processed.replace(/^#{1,6}\s+(.+)$/gm, (match, text) => {
    const level = match.match(/^#+/)[0].length;
    return `<h${Math.min(level + 2, 6)}>${text}</h${Math.min(level + 2, 6)}>`;
  });
  
  // Legacy Twine 1 syntax
  processed = processed.replace(/''([^']+)''/g, '<strong>$1</strong>');
  processed = processed.replace(/\/\/([^/]+)\/\//g, '<em>$1</em>');
  processed = processed.replace(/__([^_]+)__/g, '<u>$1</u>');
  processed = processed.replace(/==([^=]+)==/g, '<del>$1</del>');
  
  // Clean up blank lines
  processed = processed.replace(/\n{4,}/g, '\n\n\n');
  processed = processed.replace(/\n{2,3}/g, '<br><br>');
  processed = processed.replace(/\n/g, '<br>');
  
  // Restore the anchor blocks we masked above.
  processed = processed.replace(/\u0000ANC(\d+)\u0000/g, (_m, i) => __anchorMasks[Number(i)]);
  
  return processed;
};

/**
 * Generate WordPress-embeddable story HTML
 */
export const generateWordPressStoryHtml = (twee, settings = {}) => {
  const {
    linkColor = '#667eea',
    textBgColor = '#ffffff',
    frameBgColor = '#f8fafc',
    boldColor = '#fbd38d',
    italicColor = '#b794f4',
    headingColor = '#90cdf4',
    linkDisplay = 'buttons', // 'buttons' or 'inline'
    containerWidth = '100%',
    maxWidth = '900px'
  } = settings;

  // Parse Twee format
  let passages = parseTwee(twee);
  
  let startPassage = passages.find(p => p.name === 'Start');
  const storyTitle = passages.find(p => p.name === 'StoryTitle')?.content.trim() || 'Interactive Story';
  
  // If no Start passage, use the first non-StoryTitle passage
  if (!startPassage) {
    startPassage = passages.find(p => p.name !== 'StoryTitle');
  }
  
  if (!startPassage) {
    return `<div style="color:red;">No valid passages found. Make sure you have a ":: Start" passage.</div>`;
  }

  // Filter out StoryTitle from passages
  const storyPassages = passages.filter(p => p.name !== 'StoryTitle');
  
  // Ensure Start passage is first
  storyPassages.sort((a, b) => {
    if (a.name === startPassage.name) return -1;
    if (b.name === startPassage.name) return 1;
    return 0;
  });

  // Unique ID for this story instance (allows multiple stories on same page)
  const storyId = `twee-story-${Date.now()}`;

  // Generate passage HTML with processed content and link extraction
  const passagesHtml = storyPassages.map((passage, idx) => {
    let content = passage.content;
    
    // Extract links from content
    const linkRegex = /\[\[([^\]]+)\]\]/g;
    const links = [];
    let match;
    
    while ((match = linkRegex.exec(content)) !== null) {
      const linkContent = match[1];
      let label, target;
      
      if (linkContent.includes('->')) {
        [label, target] = linkContent.split('->').map(s => s.trim());
      } else if (linkContent.includes('|')) {
        [label, target] = linkContent.split('|').map(s => s.trim());
      } else {
        label = target = linkContent.trim();
      }
      links.push({ label, target });
    }
    
    // External-URL detection (auto-detects common schemes; legacy "URL:" still works)
    const EXTERNAL_RE = /^(URL:|https?:\/\/|mailto:|tel:|\/\/|#|\/)/i;
    const stripUrlPrefix = (t) => t.replace(/^URL:/i, '');

    // Remove link syntax from content for button display mode
    if (linkDisplay === 'buttons') {
      // 1) External URL links → inline <a> within the prose (preserve the
      //    surrounding sentence; do NOT add them as bottom buttons).
      content = content.replace(linkRegex, (m, linkContent) => {
        let label, target;
        if (linkContent.includes('->')) {
          [label, target] = linkContent.split('->').map(s => s.trim());
        } else if (linkContent.includes('|')) {
          [label, target] = linkContent.split('|').map(s => s.trim());
        } else {
          label = target = linkContent.trim();
        }
        if (EXTERNAL_RE.test(target)) {
          const href = stripUrlPrefix(target);
          return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="twee-inline-link twee-external-link" style="color:${linkColor};font-weight:500;text-decoration:underline;">${label}</a>`;
        }
        // internal link → leave a marker so the next pass can strip it
        return m;
      });
      // 2) Strip remaining (internal-only) [[..]] links from the prose; they
      //    will be rendered as buttons below.
      content = content.replace(linkRegex, '');
    } else {
      // Convert to inline links
      content = content.replace(linkRegex, (match, linkContent) => {
        let label, target;
        if (linkContent.includes('->')) {
          [label, target] = linkContent.split('->').map(s => s.trim());
        } else if (linkContent.includes('|')) {
          [label, target] = linkContent.split('|').map(s => s.trim());
        } else {
          label = target = linkContent.trim();
        }
        if (EXTERNAL_RE.test(target)) {
          const href = stripUrlPrefix(target);
          return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="twee-inline-link twee-external-link" style="color:${linkColor};font-weight:500;border-bottom:2px solid currentColor;text-decoration:none;">${label}</a>`;
        }
        return `<a href="javascript:void(0)" class="twee-inline-link" data-target="${target}" style="color:${linkColor};font-weight:500;border-bottom:2px solid currentColor;text-decoration:none;">${label}</a>`;
      });
    }
    
    const processedContent = processContent(content);
    
    // Bottom buttons: only INTERNAL-target links. External URLs were rendered
    // inline above so they never appear here.
    const linksHtml = linkDisplay === 'buttons' ? links
      .filter(link => !EXTERNAL_RE.test(link.target))
      .map(link => `<button class="twee-link" data-target="${link.target}">${link.label}</button>`)
      .join('') : '';
    
    return `<div class="twee-passage${idx === 0 ? ' active' : ''}" data-name="${passage.name}">
      <div class="twee-content">${processedContent}</div>
      ${linksHtml ? `<div class="twee-links">${linksHtml}</div>` : ''}
    </div>`;
  }).join('');

  return `<!-- WordPress Twee-Story Export v1.0 | Generated: ${new Date().toISOString().split('T')[0]} -->
<div id="${storyId}" class="twee-story-container">
<style>
#${storyId} {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  line-height: 1.6;
  width: ${containerWidth};
  max-width: ${maxWidth};
  margin: 0 auto;
  background: ${frameBgColor};
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}
#${storyId} .twee-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}
#${storyId} .twee-title {
  font-size: 1.5em;
  font-weight: 700;
  margin: 0;
}
#${storyId} .twee-nav {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  font-size: 0.85em;
}
#${storyId} .twee-nav-btn {
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: inherit;
  transition: background 0.2s;
}
#${storyId} .twee-nav-btn:hover {
  background: rgba(255,255,255,0.3);
}
#${storyId} .twee-nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
#${storyId} .twee-body {
  padding: 24px;
  background: ${textBgColor};
  min-height: 200px;
  color: #1e293b;
}
#${storyId} .twee-passage {
  display: none;
}
#${storyId} .twee-passage.active {
  display: block;
  animation: tweeFadeIn 0.3s ease;
}
@keyframes tweeFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
#${storyId} .twee-content {
  font-size: 1.05em;
  margin-bottom: 20px;
}
#${storyId} .twee-content strong {
  color: ${boldColor};
}
#${storyId} .twee-content em {
  color: ${italicColor};
}
#${storyId} .twee-content h3, #${storyId} .twee-content h4, #${storyId} .twee-content h5 {
  color: ${headingColor};
  margin: 16px 0 8px 0;
}
#${storyId} .twee-content ul {
  margin: 12px 0;
  padding-left: 24px;
}
#${storyId} .twee-content hr {
  border: none;
  border-top: 2px solid #e2e8f0;
  margin: 20px 0;
}
#${storyId} .twee-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}
#${storyId} .twee-link {
  background: ${linkColor};
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.95em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
#${storyId} .twee-link:hover {
  filter: brightness(0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}
#${storyId} .twee-inline-link:hover {
  opacity: 0.8;
}
#${storyId} .story-image {
  max-width: 100%;
  border-radius: 8px;
  margin: 12px 0;
}
#${storyId} .img-left {
  float: left;
  max-width: 45%;
  margin: 0 16px 12px 0;
}
#${storyId} .img-right {
  float: right;
  max-width: 45%;
  margin: 0 0 12px 16px;
}
@media (max-width: 600px) {
  #${storyId} .twee-header {
    padding: 16px 18px;
  }
  #${storyId} .twee-title {
    font-size: 1.2em;
  }
  #${storyId} .twee-body {
    padding: 18px;
  }
  #${storyId} .img-left, #${storyId} .img-right {
    float: none;
    max-width: 100%;
    margin: 12px 0;
  }
}
</style>

<div class="twee-header">
  <h2 class="twee-title">${storyTitle}</h2>
  <div class="twee-nav">
    <button class="twee-nav-btn" id="${storyId}-back" disabled>← Back</button>
    <button class="twee-nav-btn" id="${storyId}-restart">Restart</button>
  </div>
</div>

<div class="twee-body">
  ${passagesHtml}
</div>

<script>
(function(){
  'use strict';
  var container = document.getElementById('${storyId}');
  var passages = container.querySelectorAll('.twee-passage');
  var backBtn = document.getElementById('${storyId}-back');
  var restartBtn = document.getElementById('${storyId}-restart');
  var history = [];
  var startName = '${startPassage.name}';
  
  function showPassage(name, addToHistory) {
    var found = null;
    passages.forEach(function(p) {
      if (p.getAttribute('data-name') === name) {
        found = p;
      }
      p.classList.remove('active');
    });
    if (found) {
      found.classList.add('active');
      if (addToHistory !== false) {
        history.push(name);
      }
      backBtn.disabled = history.length <= 1;
      container.querySelector('.twee-body').scrollTop = 0;
    }
  }
  
  function goBack() {
    if (history.length > 1) {
      history.pop();
      showPassage(history[history.length - 1], false);
    }
  }
  
  function restart() {
    history = [];
    showPassage(startName, true);
  }
  
  // Event delegation for link clicks
  container.addEventListener('click', function(e) {
    var link = e.target.closest('.twee-link, .twee-inline-link');
    if (!link) return;
    var target = link.getAttribute('data-target');
    // External anchors have an href but no data-target — let the browser
    // handle them (target="_blank" opens YouTube etc. normally).
    if (!target) return;
    e.preventDefault();
    showPassage(target, true);
  });
  
  backBtn.addEventListener('click', goBack);
  restartBtn.addEventListener('click', restart);
  
  // Initialize
  showPassage(startName, true);
})();
</script>
</div>`;
};

// Fallback copy function for when Clipboard API is blocked
const fallbackCopyToClipboard = (text) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
    return true;
  } catch (err) {
    console.error('Fallback copy failed:', err);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

/**
 * Copy WordPress Story HTML to clipboard
 */
export const copyWordPressStoryHtml = async (twee, settings = {}) => {
  const html = generateWordPressStoryHtml(twee, settings);
  try {
    await navigator.clipboard.writeText(html);
  } catch (err) {
    // Fallback for when Clipboard API is blocked
    if (!fallbackCopyToClipboard(html)) {
      throw new Error('Unable to copy to clipboard. Please try again.');
    }
  }
  return html;
};
